import { useMediaQuery } from '@mantine/hooks'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useCallback } from 'react'
import { useSpring } from 'react-spring'

import Button from '@/features/common/components/Button'
import { requestProgressNow } from '@/features/klass/api'
import { useProgressRate } from '@/features/klass/hooks/useProgressRate'
import { SolidPlayIcon } from '@/shared/layout/WithCurriculumLayout/Icons'

import CurriculumKlassProgressbar from '../CurriculumKlassProgressbar'

interface ICurriculumKlassCardProps {
  curriculumId: string
  courseId: string
  isActive: boolean
  imageUrl: string
  title: string
}

const CurriculumKlassCard = ({
  curriculumId,
  courseId,
  isActive,
  imageUrl,
  title,
}: ICurriculumKlassCardProps) => {
  const router = useRouter()
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const goToPlayer = useCallback(async () => {
    try {
      const { chapterId, id: resourceId } = await requestProgressNow(courseId)
      void router.push(
        `${curriculumId}/course/${courseId}/${chapterId as string}/${resourceId as string}`,
      )
    } catch (e) {
      console.error(e)
    }
  }, [courseId])

  const {
    rate,
    resourceCompletedSize: allResourceCompletedSize,
    resourceCompletedSeconds: allResourceCompletedSeconds,
    curEnrollment,
  } = useProgressRate(courseId)

  const { number: progress } = useSpring({
    from: { number: 0 },
    number: rate,
    delay: 100,
  })

  return (
    <div className="relative flex w-full flex-col items-center gap-3 rounded-2xl border bg-white p-4 md:w-[calc(50%-8px)] lg:static lg:w-full lg:flex-row lg:gap-6 lg:rounded-xl">
      {imageUrl && (
        <div className="absolute left-4 top-4 h-[72px] w-[72px] shrink-0 lg:relative lg:left-0 lg:top-0 lg:h-[100px] lg:w-[100px]">
          <Image
            src={imageUrl}
            alt={`thumbnail_${courseId}`}
            className="rounded-lg"
            width={100}
            height={100}
          />
        </div>
      )}
      <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-1 lg:gap-[9px] lg:py-2">
        <div className="flex h-[72px] items-center pl-[84px] lg:h-[54px] lg:items-start lg:pl-0">
          <h6 className="line-clamp-2 text-base font-semibold leading-normal text-label-normal lg:text-lg">
            {title}
          </h6>
        </div>
        <div className="flex w-full flex-row items-center gap-2 py-1 lg:py-0">
          <CurriculumKlassProgressbar rate={rate as number} progress={progress} />
          <hr className="h-3 border-l border-gray-assistive" />
          <p className="text-sm font-semibold">
            <span className="text-primary-normal">{allResourceCompletedSize ?? 0}강</span>
            <span className="text-label-alternative">/{curEnrollment?.totalResources}강</span>
          </p>
        </div>
      </div>

      <Button
        size={isMobile ? 'lg' : 'xl'}
        text="수강하기"
        disabled={!isActive}
        theme="primary"
        customEvent={goToPlayer}
        className="lg:w-[140px] lg:shrink-0"
      />
    </div>
  )
}

export default CurriculumKlassCard
